import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import Image from '../../Image/Image';
import { ModulorButtonWithAnalytics, VARIANTS } from '../../Modulor/Button';
import Loader from '../../Loader/Loader';
import { Local } from '../../Local/Local';
import NoContentPlaceholder from '../../NoContentPlaceholder/NoContentPlaceholder';
import { EVENT_ACTION } from '../../../constants/ga';
import { gameImageSelector } from '../../../redux/reducers/app/configuration';
import { getGameImagePath } from '../../../utils/imagePath';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREA_DETAILS,
} from '../../../constants/analytics';
import styles from './GameLaunchPreviewDesktop.module.css';
import { useIsSC } from '../../../hooks/useIsSC';
import FEATURES from '../../../constants/featureToggling';

const GameLaunchPreviewDesktop = ({
  game,
  play,
  playDemo,
  isLoading,
  isAvailable,
  className,
  isMaximized,
  analyticsData,
}) => {
  const isEnabledDemoMode =
    useFeatureIsOn(FEATURES.ENABLE_DEMO_MODE) && game.demoModeAvailable;
  const gameImagePath = useSelector(gameImageSelector);
  const isSC = useIsSC();

  return (
    <div
      className={classNames(
        styles.component,
        className,
        isMaximized && styles.fullscreen
      )}
    >
      <Image
        src={getGameImagePath(gameImagePath, game?.id, true)}
        className={styles.img}
      />

      {isLoading && (
        <div className={styles.content}>
          <Loader />
        </div>
      )}

      {!isLoading && isAvailable && (
        <div className={classNames(styles.content, isSC && styles.content_sc)}>
          {isEnabledDemoMode && (
            <ModulorButtonWithAnalytics
              onClick={playDemo}
              dataLayerEventData={{
                eventAction: EVENT_ACTION.GAME_PREVIEW_PAGE_DEMO,
                game_id: game?.id,
                provider_id: game?.provider,
              }}
              analyticsData={{
                ...analyticsData,
                schema: ANALYTICS_SCHEMA_URLS.PLAY_DEMO_GAME_CLICK,
                screen_area_detail:
                  ANALYTICS_SCREEN_AREA_DETAILS.PREVIEW_ACTIONS,
                game_id: game?.id,
                provider_id: game?.provider,
              }}
              className={styles.demo}
              variant={VARIANTS.SECONDARY}
            >
              <Local>Demo</Local>
            </ModulorButtonWithAnalytics>
          )}

          <ModulorButtonWithAnalytics
            onClick={play}
            dataLayerEventData={{
              eventAction: EVENT_ACTION.GAME_PREVIEW_PAGE_REAL,
              game_id: game?.id,
              provider_id: game?.provider,
            }}
            analyticsData={{
              ...analyticsData,
              schema: ANALYTICS_SCHEMA_URLS.PLAY_GAME_CLICK,
              screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.PREVIEW_ACTIONS,
              game_id: game?.id,
              provider_id: game?.provider,
            }}
            className={styles.play}
          >
            <Local>Play</Local>
          </ModulorButtonWithAnalytics>
        </div>
      )}

      {!isLoading && !isAvailable && (
        <NoContentPlaceholder
          className={classNames(styles.content, isSC && styles.contentWhiteBg)}
          title="The game is temporarily unavailable, but will be back soon"
          description={null}
        />
      )}
    </div>
  );
};

GameLaunchPreviewDesktop.propTypes = {
  isMaximized: PropTypes.bool,
  game: PropTypes.shape({
    id: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    status: PropTypes.string,
    category: PropTypes.array,
    provider: PropTypes.string,
    translationKey: PropTypes.string,
    demoModeAvailable: PropTypes.bool,
  }),
  play: PropTypes.func,
  playDemo: PropTypes.func,
  isLoading: PropTypes.bool,
  isAvailable: PropTypes.bool,
  className: PropTypes.string,
};

GameLaunchPreviewDesktop.defaultProps = {
  game: {},
  isLoading: true,
  isAvailable: true,
};

export default GameLaunchPreviewDesktop;
